import { coachShortDisplayName } from '@efdigital/ll-addon-coaching';
import type { CoachProfile } from '@efdigital/ll-addon-data';
import type { Coaching } from '@efdigital/ll-addon-data/types/coaching/coaching';
import { AdminActionModal } from '@efdigital/ll-admin-v2/components/coaching/sessions/detail/admin-action-modal.gts';
import { queryCoachingSessions } from '@efdigital/ll-admin-v2/data/builders/coaching/coaching-sessions';
import { queryCoachProfilesByUserIds } from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import eq from '@efdigital/ll-admin-v2/helpers/eq.ts';
import type AdminAiMatchingService from '@efdigital/ll-admin-v2/services/admin-ai-matching';
import type AdminSessionsService from '@efdigital/ll-admin-v2/services/admin-sessions';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

/**
 * Statuses the reassignment will clear, mirroring the backend's own
 * `LIVE_SESSION_STATUSES` so the warning matches what actually happens.
 *
 * Deliberately not the addon's `isHistory`/`isCancelled` pair: those answer
 * "where does this session belong in a list?", and they disagree with the
 * backend on two statuses that matter here — `external_change_flagged` is
 * history to them but is cancelled by a reassignment, and `booking_abandoned`
 * is neither history nor cancelled to them but is terminal and left alone.
 */
const LIVE_SESSION_STATUSES = new Set([
  'booking_pending',
  'awaiting_coach_confirmation',
  'scheduled',
  'external_change_flagged',
]);

/** Enough sessions to count the live ones — a plan's total is quota-bounded. */
const SESSION_SCAN_LIMIT = 200;

/** An eligible replacement coach, resolved for the picker. */
type EligibleCoach = {
  coachUserId: string;
  name: string;
  relevance?: number;
};

/** Loaded together when the modal opens, so the fields block waits once. */
type ReassignContext = {
  eligibleCoaches: EligibleCoach[];
  /** How many sessions with the current coach will be cancelled. */
  liveSessionCount: number;
  /** False when ranking could not be loaded — picker falls back to A–Z names. */
  matchesAvailable: boolean;
};

/**
 * What the admin is asking for. `newCoachUserId` empty means unassign — the two
 * are one value rather than a mode flag plus a field, so the modal can never be
 * in the contradictory state of "unassign" with a coach selected.
 */
export type ReassignCoachValue = {
  newCoachUserId: string;
  reason: string;
};

interface ReassignCoachModalSignature {
  Args: {
    /** Plan whose coach is changing. */
    planId: string;
    /** The cohort's coaching record — supplies the coach pool to choose from. */
    coaching: Coaching;
    /** Used with `coacheeId` to rank the picker from the same matches the coachee sees. */
    cohortId?: string;
    coacheeId?: string;
    /** Current coach, excluded from the picker. Empty when the plan has no chosen coach. */
    currentCoachUserId: string;
    currentCoachName: string;
    /** The current (`chosen`) partnership, whose sessions will be cleared. */
    partnershipId: string;
    /** Pre-selects a coach when Assign is opened from the ranked list. */
    initialCoachUserId?: string;
    onSuccess?: () => Promise<void>;
  };
  Blocks: {
    trigger: [showModal: () => void];
  };
}

/**
 * Admin action to move a coachee onto a different coach, or off their coach
 * entirely — including a first assignment when the plan has no chosen coach.
 *
 * Built for conflicts of interest — two coachees in the same team ending up with
 * the same coach, so one has to move. That framing drives the copy: nothing here
 * suggests the outgoing coach did anything wrong.
 *
 * Replacements are limited to the cohort's own coach pool, and within it to
 * coaches with a published profile, because the booking rules require one — an
 * unpublished coach would leave the coachee unable to book with their new coach.
 */
export class ReassignCoachModal extends Component<ReassignCoachModalSignature> {
  @service declare store: Store;
  @service declare adminSessions: AdminSessionsService;
  @service declare adminAiMatching: AdminAiMatchingService;

  get initialValue(): ReassignCoachValue {
    return {
      newCoachUserId: this.args.initialCoachUserId ?? '',
      reason: '',
    };
  }

  get hasCurrentCoach(): boolean {
    return Boolean(this.args.currentCoachUserId);
  }

  get title(): string {
    return this.hasCurrentCoach ? 'Reassign coach' : 'Assign coach';
  }

  get submitLabel(): string {
    return this.hasCurrentCoach ? 'Reassign coach' : 'Assign coach';
  }

  get submittingLabel(): string {
    return this.hasCurrentCoach ? 'Reassigning…' : 'Assigning…';
  }

  get submitButtonClass(): string {
    return this.hasCurrentCoach ? 'btn-danger' : 'btn-primary';
  }

  /**
   * Loads the picker options and the number of sessions about to be cancelled.
   *
   * Runs on demand from the fields block, so an unopened modal costs nothing on
   * a page that lists every coachee in a cohort.
   */
  loadContext = async (): Promise<ReassignContext> => {
    const [eligible, liveSessionCount] = await Promise.all([
      this.loadEligibleCoaches(),
      this.loadLiveSessionCount(),
    ]);
    return {
      eligibleCoaches: eligible.coaches,
      liveSessionCount,
      matchesAvailable: eligible.matchesAvailable,
    };
  };

  private async loadEligibleCoaches(): Promise<{
    coaches: EligibleCoach[];
    matchesAvailable: boolean;
  }> {
    const pool = await this.args.coaching.coachPool;
    const poolUserIds = (pool?.userIds ?? []).filter(
      (userId) => userId && userId !== this.args.currentCoachUserId
    );
    if (!poolUserIds.length) {
      return { coaches: [], matchesAvailable: false };
    }

    const result = await this.store.request(
      queryCoachProfilesByUserIds(poolUserIds)
    );
    const published = (result.content.data ?? []).filter(
      (profile: CoachProfile) => profile.status === 'published'
    );

    const byUser = new Map<string, EligibleCoach>();
    for (const profile of published) {
      if (!byUser.has(profile.user)) {
        byUser.set(profile.user, {
          coachUserId: profile.user,
          name: coachShortDisplayName(profile),
        });
      }
    }

    const coaches = [...byUser.values()];
    const { cohortId, coacheeId } = this.args;
    if (!cohortId || !coacheeId) {
      return {
        coaches: coaches.sort((a, b) => a.name.localeCompare(b.name)),
        matchesAvailable: false,
      };
    }

    try {
      const matches = await this.adminAiMatching.getCoachMatches(
        cohortId,
        coacheeId
      );
      const relevanceById = new Map(
        matches.map((match) => [match.coachId, match.relevance])
      );
      return {
        coaches: coaches
          .map((coach) => ({
            ...coach,
            relevance: relevanceById.get(coach.coachUserId),
          }))
          .sort((a, b) => {
            const aRelevance = a.relevance ?? -1;
            const bRelevance = b.relevance ?? -1;
            if (bRelevance !== aRelevance) {
              return bRelevance - aRelevance;
            }
            return a.name.localeCompare(b.name);
          }),
        matchesAvailable: true,
      };
    } catch {
      return {
        coaches: coaches.sort((a, b) => a.name.localeCompare(b.name)),
        matchesAvailable: false,
      };
    }
  }

  private async loadLiveSessionCount(): Promise<number> {
    if (!this.args.partnershipId) {
      return 0;
    }

    const result = await this.store.request(
      queryCoachingSessions(
        { partnershipId: this.args.partnershipId },
        { offset: 0, limit: SESSION_SCAN_LIMIT }
      )
    );
    return (result.content.data ?? []).filter((session) =>
      LIVE_SESSION_STATUSES.has(session.status)
    ).length;
  }

  isValid = (value: ReassignCoachValue): boolean => {
    if (!value.reason.trim()) {
      return false;
    }
    if (!this.hasCurrentCoach && !value.newCoachUserId) {
      return false;
    }
    return true;
  };

  get invalidHint(): string {
    return this.hasCurrentCoach
      ? 'Enter a reason to continue'
      : 'Choose a coach and enter a reason to continue';
  }

  reassign = async (value: ReassignCoachValue) => {
    await this.adminSessions.reassignCoach(this.args.planId, {
      // An empty selection is an unassign, which the endpoint expects as an
      // omitted field rather than an empty string.
      newCoachUserId: value.newCoachUserId || undefined,
      reason: value.reason.trim(),
    });
    await this.args.onSuccess?.();
  };

  setCoachFromSelect = (
    value: ReassignCoachValue,
    setValue: (value: ReassignCoachValue) => void,
    event: Event
  ) => {
    setValue({
      ...value,
      newCoachUserId: (event.target as HTMLSelectElement).value,
    });
  };

  setReasonFromInput = (
    value: ReassignCoachValue,
    setValue: (value: ReassignCoachValue) => void,
    event: Event
  ) => {
    setValue({
      ...value,
      reason: (event.target as HTMLTextAreaElement).value,
    });
  };

  optionLabel = (coach: EligibleCoach, matchesAvailable: boolean): string => {
    if (!matchesAvailable) {
      return coach.name;
    }
    if (coach.relevance === undefined) {
      return `${coach.name} — unranked`;
    }
    return `${coach.name} — ${Math.round(coach.relevance)}% match`;
  };

  <template>
    <AdminActionModal
      @title={{this.title}}
      @submitLabel={{this.submitLabel}}
      @submittingLabel={{this.submittingLabel}}
      @submitButtonClass={{this.submitButtonClass}}
      @initialValue={{this.initialValue}}
      @isValid={{this.isValid}}
      @invalidHint={{this.invalidHint}}
      @onSubmit={{this.reassign}}
    >
      <:trigger as |showModal|>
        {{yield showModal to="trigger"}}
      </:trigger>
      <:description>
        {{#if this.hasCurrentCoach}}
          Moves this coachee to a different coach, or off
          {{@currentCoachName}}
          without naming a replacement. Use this for a conflict of interest —
          two coachees in the same team sharing a coach, say — not to record a
          coach being rejected. The outgoing partnership is marked
          <strong>reassigned</strong>, never declined, and
          {{@currentCoachName}}
          is not shown as rejected anywhere.
        {{else}}
          Assigns a coach from this cohort's pool. The coachee is moved straight
          to
          <strong>chosen</strong>
          with that coach — the same outcome as if they had picked them.
        {{/if}}
      </:description>
      <:fields as |value setValue isSubmitting|>
        <Await @promise={{(this.loadContext)}}>
          <:pending>
            <p class="text-muted small">Loading coach pool…</p>
          </:pending>
          <:success as |context|>
            <div class="mb-3">
              <label class="form-label" for="reassign-new-coach">New coach</label>
              <select
                id="reassign-new-coach"
                class="form-select"
                disabled={{isSubmitting}}
                data-test-reassign-new-coach
                {{on "change" (fn this.setCoachFromSelect value setValue)}}
              >
                {{#if this.hasCurrentCoach}}
                  <option value="" selected={{eq value.newCoachUserId ""}}>Don't
                    assign a coach — let the coachee choose again</option>
                {{else}}
                  <option
                    value=""
                    selected={{eq value.newCoachUserId ""}}
                  >Select a coach</option>
                {{/if}}
                {{#each context.eligibleCoaches as |coach|}}
                  <option
                    value={{coach.coachUserId}}
                    selected={{eq value.newCoachUserId coach.coachUserId}}
                  >{{this.optionLabel coach context.matchesAvailable}}</option>
                {{/each}}
              </select>
              {{#if context.eligibleCoaches.length}}
                <div class="form-text">Only coaches in this cohort's pool with a
                  published profile can be assigned, since the coachee has to be
                  able to book with them.{{#if context.matchesAvailable}}
                    Ranked by the same match scores the coachee sees.
                  {{else}}
                    Match ranking was unavailable, so coaches are listed
                    alphabetically.
                  {{/if}}</div>
              {{else}}
                <div class="form-text text-warning-emphasis">No
                  {{if this.hasCurrentCoach "other "}}coach in this cohort's
                  pool has a published profile{{if
                    this.hasCurrentCoach
                    ", so only unassigning is available"
                    ""
                  }}.</div>
              {{/if}}
            </div>

            {{#if this.hasCurrentCoach}}
              {{#unless value.newCoachUserId}}
                <div class="alert alert-info" role="alert">
                  <p class="mb-0">The coachee will have no coach and picks again
                    from the start. Their chemistry call allowance is restored
                    automatically, so they can shortlist coaches even if they
                    had used it all up.</p>
                </div>
              {{/unless}}
            {{/if}}

            {{#if context.liveSessionCount}}
              <div class="alert alert-warning" role="alert">
                <p class="mb-0">
                  <strong>{{context.liveSessionCount}}
                    {{if
                      (eq context.liveSessionCount 1)
                      "session"
                      "sessions"
                    }}</strong>
                  with
                  {{@currentCoachName}}
                  will be cancelled, and both parties emailed. The coachee's
                  quota is credited back in full — no late-cancellation penalty
                  applies.
                </p>
              </div>
            {{/if}}
          </:success>
          <:error>
            <p class="text-danger small">Couldn't load the cohort's coach pool.
              {{#if this.hasCurrentCoach}}You can still unassign without naming
                a replacement.{{/if}}</p>
          </:error>
        </Await>

        <div class="mb-3">
          <label
            class="form-label required"
            for="reassign-reason"
          >Reason</label>
          <textarea
            id="reassign-reason"
            class="form-control"
            rows="3"
            required
            placeholder="e.g. Conflict of interest — same team as another coachee"
            disabled={{isSubmitting}}
            data-test-reassign-reason
            {{on "input" (fn this.setReasonFromInput value setValue)}}
          >{{value.reason}}</textarea>
          <div class="form-text">Included in the emails to the coachee and both
            coaches.</div>
        </div>
      </:fields>
    </AdminActionModal>
  </template>
}
